import {
  type AskResponse,
  formatMinor,
  type PulseCard,
  PULSE_TIER_LABELS,
  PULSE_TIER_ORDER,
  type PulseTier,
  QUESTION_EXAMPLES,
  QUESTION_INTENTS,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, cn, Input, Pill, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  askQuestion,
  dismissPulseSignal,
  pulseKeys,
  pulseQuery,
} from '../../shared/api/pulse-queries.js';

/**
 * The Financial Pulse.
 *
 * The first screen, and for most mornings the only one. It answers a single
 * question, what matters today, with at most five cards, grouped by what the
 * reader should do about them rather than by how the news feels.
 *
 * Every figure on this page was computed in SQL. The one paragraph a model
 * writes is in the answer panel, sitting beside the numbers rather than in
 * place of them.
 */

/**
 * A card wears its tier as a left accent bar rather than a tinted wash: four
 * washed-out pastel cards in a stack read as wallpaper, four white cards with
 * one coloured edge each read as a list of distinct things to do.
 */
const TIER_ACCENTS: Readonly<Record<PulseTier, string>> = {
  urgent: 'before:bg-danger',
  costing: 'before:bg-warning',
  opportunity: 'before:bg-success',
  fyi: 'before:bg-content-subtle/40',
};

const TIER_DOTS: Readonly<Record<PulseTier, string>> = {
  urgent: 'bg-danger',
  costing: 'bg-warning',
  opportunity: 'bg-success',
  fyi: 'bg-content-subtle',
};

export function PulsePage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const pulse = useQuery(pulseQuery(organizationId, { limit: 5 }));

  const dismiss = useMutation({
    mutationFn: (card: PulseCard) =>
      dismissPulseSignal(organizationId, { signalKey: card.key, subjectKey: card.subjectKey }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: pulseKeys.all(organizationId) });
    },
  });

  const { ask } = useSearch({ strict: false });

  const greeting =
    session.user.fullName === null ? 'Good morning' : `Good morning, ${session.user.fullName}`;

  const cards = pulse.data?.cards ?? [];
  const byTier = PULSE_TIER_ORDER.map((tier) => ({
    tier,
    cards: cards.filter((card) => card.tier === tier),
  })).filter((group) => group.cards.length > 0);

  return (
    <div className="stagger-rise mx-auto flex w-full max-w-3xl flex-col gap-8">
      <div className="pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-600">
          {new Date().toLocaleDateString('en-CA', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
        </p>
        <h1 className="bg-gradient-to-r from-content to-content/70 bg-clip-text text-4xl font-bold tracking-tight text-transparent">
          {greeting}.
        </h1>
        <p className="mt-2 text-[15px] text-content-muted">
          {pulse.isPending
            ? 'Looking at the books…'
            : cards.length === 0
              ? 'Nothing needs you today.'
              : "Here's what matters."}
        </p>
      </div>

      {pulse.isPending ? (
        <div className="flex flex-col gap-2" aria-label="Reading the books">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-5/6" />
        </div>
      ) : null}

      {pulse.isError ? (
        <Alert tone="danger" title="The briefing could not be built">
          {pulse.error instanceof Error ? pulse.error.message : 'Unknown error.'}
        </Alert>
      ) : null}

      {byTier.map((group) => (
        <section key={group.tier} className="flex flex-col gap-2">
          <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-content-subtle">
            <span className={cn('inline-block size-2 rounded-full', TIER_DOTS[group.tier])} />
            {PULSE_TIER_LABELS[group.tier]}
          </h2>

          {group.cards.map((card) => (
            <article
              key={`${card.key}:${card.subjectKey}`}
              className={cn(
                'relative overflow-hidden rounded-[var(--radius-surface)] border border-border bg-surface',
                'px-5 py-4 shadow-[var(--shadow-card)]',
                'before:absolute before:inset-y-0 before:left-0 before:w-1',
                TIER_ACCENTS[card.tier],
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-content">{card.headline}</p>
                  {card.detail === null ? null : (
                    <p className="mt-1.5 text-sm leading-relaxed text-content-muted">
                      {card.detail}
                    </p>
                  )}
                  {card.dueOn === null ? null : (
                    <p className="mt-2">
                      <Pill tone="warning">Due {card.dueOn}</Pill>
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => dismiss.mutate(card)}
                  // Not "never show me this again": it comes back if it grows
                  // materially worse, and the button should not promise
                  // otherwise.
                  title="Hide this for a week"
                >
                  Hide
                </Button>
              </div>
            </article>
          ))}
        </section>
      ))}

      {pulse.data === undefined || pulse.data.totalSignals <= pulse.data.cards.length ? null : (
        <p className="text-xs text-content-subtle">
          {pulse.data.totalSignals - pulse.data.cards.length} other thing
          {pulse.data.totalSignals - pulse.data.cards.length === 1 ? '' : 's'} worth knowing are not
          shown. Nothing is hidden from the pages themselves.
        </p>
      )}

      <AskPanel organizationId={organizationId} initialQuestion={ask ?? null} />
    </div>
  );
}

/** A jsonb cell is whatever the query put in it; only scalars are displayable. */
function renderCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function AskPanel({
  organizationId,
  initialQuestion,
}: {
  organizationId: string;
  initialQuestion: string | null;
}) {
  const [question, setQuestion] = useState(initialQuestion ?? '');
  const [answer, setAnswer] = useState<AskResponse | null>(null);

  const ask = useMutation({
    mutationFn: (value: string) => askQuestion(organizationId, { question: value }),
    onSuccess: setAnswer,
  });

  // A question arriving from ⌘K was already committed to by pressing Enter
  // there; making the person press it twice would be pure ceremony.
  const firedRef = useRef(false);
  useEffect(() => {
    if (initialQuestion !== null && initialQuestion.length >= 3 && !firedRef.current) {
      firedRef.current = true;
      ask.mutate(initialQuestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (question.trim().length >= 3) ask.mutate(question.trim());
          }}
        >
          <Input
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask about the books…"
            aria-label="Ask a question about the books"
          />
          <Button type="submit" isLoading={ask.isPending} disabled={question.trim().length < 3}>
            Ask
          </Button>
        </form>

        {answer === null ? (
          <div className="flex flex-wrap gap-2">
            {QUESTION_INTENTS.slice(0, 3).map((intent) => (
              <button
                key={intent}
                type="button"
                onClick={() => {
                  setQuestion(QUESTION_EXAMPLES[intent]);
                  ask.mutate(QUESTION_EXAMPLES[intent]);
                }}
                className="rounded-full border border-border px-3 py-1 text-xs text-content-muted hover:bg-surface-sunken"
              >
                {QUESTION_EXAMPLES[intent]}
              </button>
            ))}
          </div>
        ) : !answer.understood ? (
          <div>
            <p className="text-sm text-content">{answer.message}</p>
            <ul className="mt-2 list-inside list-disc text-sm text-content-muted">
              {answer.suggestions.map((suggestion) => (
                <li key={suggestion}>{suggestion}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/* The computed sentence first. The model's paragraph sits under
                it, never in place of it. */}
            <p className="font-medium text-content">{answer.headline}</p>

            {/* Which period the figures cover, said before the prose rather
                than buried inside it. When the question named no period the
                engine still had to choose one, and a screen that does not
                admit that is answering a different question confidently. */}
            <p className="text-xs text-content-subtle">
              {answer.periodAssumed ? (
                <>
                  You did not say when, so this covers{' '}
                  <span className="tabular">
                    {answer.periodFrom} to {answer.periodTo}
                  </span>
                  . Name a period and it will use that instead.
                </>
              ) : (
                <>
                  {answer.periodPhrase === null
                    ? 'Covering'
                    : `Reading "${answer.periodPhrase}" as`}{' '}
                  <span className="tabular">
                    {answer.periodFrom} to {answer.periodTo}
                  </span>
                  .
                </>
              )}
            </p>

            {answer.narration === null ? null : (
              <p className="text-sm text-content-muted">{answer.narration}</p>
            )}

            {answer.rows.length === 0 ? null : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-content-subtle">
                      {Object.keys(answer.rows[0] ?? {}).map((column) => (
                        <th key={column} className="px-2 py-1 font-semibold">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {answer.rows.slice(0, 10).map((row, index) => (
                      <tr key={index} className="border-b border-border last:border-0">
                        {Object.entries(row).map(([column, value]) => (
                          <td key={column} className="tabular px-2 py-1">
                            {typeof value === 'number' && /total|amount/i.test(column)
                              ? formatMinor(String(value))
                              : renderCell(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-content-subtle">
              Counted from {renderCell(answer.evidence['basis']) || 'the books'}. The figures above
              are computed; the paragraph is written from them.
            </p>
          </div>
        )}

        {ask.isError ? (
          <p className="text-xs text-danger">
            {ask.error instanceof Error ? ask.error.message : 'That question could not be asked.'}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
